import React, { useEffect, useState } from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { useCampuses } from '../../contexts/CampusContext';
import { useEvents } from '../../contexts/EventsContext';
import { useLeaders } from '../../contexts/LeaderContext';
import { useMinistries } from '../../contexts/MinistryContext';
import MinistryLayout from './MinistryLayout';

const YouthMinistry = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ministryId = queryParams.get('ministryId');
  const {leaders} = useLeaders();
  const {campuses} = useCampuses();
  const {ministries} = useMinistries();
  const [ministryLeaders, setMinistryLeaders] = useState([]);
  const [ministry, setMinistry] = useState({});
  const [ministryEvents, setMinistryEvents] = useState([]);
  const {events} = useEvents();

  useEffect(() => {
    if (leaders && campuses && ministries) {
      // Enhance leaders with campus names
      const enhancedLeaders = leaders
        .filter(leader => leader.ministryId === ministryId)
        .map(leader => {
          // Find the campus for this leader
          const campus = campuses.find(c => c.id === leader.campusId);
          // Return new object with campusName added
          return {
            ...leader,
            campusName: campus ? campus.name : 'Unknown Campus'
          };
        });
      
      setMinistryLeaders(enhancedLeaders);
      
      // Find the current ministry
      const currentMinistry = ministries.find(m => m.id === ministryId);
      setMinistry(currentMinistry || {});
    }
  }, [leaders, campuses, ministries, ministryId]);
  
  useEffect(() => {
    if (events && ministry.name) {
      const filteredEvents = events.filter(event => {
        // Skip if event is missing required fields
        if (!event || !event.targetGroup) return false;
        
        // Compare with ministry name (case insensitive)
        return event.targetGroup.toLowerCase() === ministry.name.toLowerCase();
      });
      
      // Sort by date (newest first)
      const sortedEvents = filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setMinistryEvents(sortedEvents);
    } else {
      setMinistryEvents([]);
    }
  }, [events, ministry.name]);

  
  return (
    <MinistryLayout
      title="Youth Ministry"
      description="Guiding and empowering the next generation to develop a personal relationship with Jesus Christ."
      leaderName="Michael Thompson"
      leaderTitle="Youth Pastor"
      leaders= {ministryLeaders}
      coverImage={ministry.cover_image}
      vision="To raise up a generation of youth who are passionate about Jesus, grounded in the Word, and equipped to impact their world for Christ."
      mission="To create an environment where youth can encounter God, build meaningful relationships, and develop as disciples who live out their faith with boldness."
      activities={[
        "Weekly youth services with engaging worship and Bible teaching",
        "Small group discipleship programs for deeper spiritual growth",
        "Monthly outreach and community service projects",
        "Annual youth camp and leadership retreats",
        "Creative arts team (music, dance, drama)",
        "Sports and social activities that build community",
        "Mentoring program connecting youth with adult role models"
      ]}
      schedules={ministryEvents}
    >
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Current Series</h3>
        <div className="flex flex-col md:flex-row gap-6">
          <img 
            src={`${process.env.PUBLIC_URL}/images/youth-series.jpg`} 
            alt="Youth Series" 
            className="w-full md:w-1/3 rounded-lg object-cover h-48"
          />
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">FEARLESS: Living Bold for God</h4>
            <p className="text-gray-700 mb-4">
              In a world that pressures us to conform, how can we stand firm in our faith? This 6-week series explores biblical characters who demonstrated extraordinary courage in the face of opposition and shows how we can live with the same boldness today.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded">Identity</span>
              <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded">Courage</span>
              <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded">Purpose</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
  <h3 className="text-xl font-semibold text-gray-800 mb-4">Upcoming Events</h3>
  
  {ministryEvents.length > 0 ? (
    <ul className="space-y-4">
      {ministryEvents.map(event => (
        <li key={event.id} className="border-b pb-3 border-gray-200 last:border-0">
          <h4 className="font-medium text-gray-900">{event.name}</h4>
          <p className="text-gray-600">
            {new Date(event.date).toLocaleDateString()} • {event.time}
          </p>
          <p className="text-gray-700">{event.description}</p>
          {event.location && (
            <p className="text-sm text-gray-500 mt-1">
              <FaMapMarkerAlt className="inline mr-1" />
              {event.location}
            </p>
          )}
        </li>
      ))}
    </ul>
  ) : (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
      <svg 
        className="mx-auto h-12 w-12 text-blue-400" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
      <h4 className="mt-2 text-lg font-medium text-gray-900">No Upcoming Events</h4>
      <p className="mt-1 text-sm text-gray-600">
        Check back later for upcoming {ministry.name} events!
      </p>
    </div>
  )}
</div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Get Connected</h3>
          <p className="text-gray-700 mb-4">
            Join our youth community on social media to stay updated on events, share encouragement, and connect with other youth.
          </p>
          <div className="flex space-x-4">
            <a href="#" className="text-blue-600 hover:text-blue-800">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="#" className="text-pink-600 hover:text-pink-800">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </MinistryLayout>
  );
};

export default YouthMinistry;
